import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp, Target, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { fetchGlobalSideBets, fetchGlobalSideBetPicks, pickGlobalSideBet, fetchSideBetPicksForGroup } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import type { GlobalSideBet, GlobalSideBetPick } from '@/types/side-bets';
import { PicksRevealSheet, type RevealPick } from '@/components/PicksRevealSheet';
import { RevealHighlight } from '@/components/RevealHighlight';

function formatDeadline(deadline: string): string {
  const d = new Date(deadline);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMs < 0) return 'פג תוקף';
  if (diffDays >= 1) return `נסגר בעוד ${diffDays} ימים`;
  if (diffHours >= 1) return `נסגר בעוד ${diffHours} שעות`;
  return 'נסגר בקרוב';
}

const ROUND_LABELS: Record<number, string> = {
  1: 'סיבוב ראשון',
  2: 'חצי גמר אזוריים',
  3: 'גמרים אזוריים',
  4: 'גמר ה-NBA',
};

interface SideBetsFullProps {
  groupId: string;
}

const SideBetsFull: React.FC<SideBetsFullProps> = ({ groupId }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bets, setBets] = useState<GlobalSideBet[]>([]);
  const [picksMap, setPicksMap] = useState<Record<string, GlobalSideBetPick>>({});
  const [savingPick, setSavingPick] = useState<string | null>(null); // bet ID being saved
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});
  const [collapsedRounds, setCollapsedRounds] = useState<Set<number>>(new Set());
  const [totalMembers, setTotalMembers] = useState(0);
  const [revealBet, setRevealBet] = useState<GlobalSideBet | null>(null);
  const [revealPicks, setRevealPicks] = useState<RevealPick[]>([]);
  const [revealLoading, setRevealLoading] = useState(false);
  const otherInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleRevealBet = async (bet: GlobalSideBet) => {
    setRevealBet(bet);
    setRevealLoading(true);
    setRevealPicks([]);
    try {
      const picks = await fetchSideBetPicksForGroup(groupId, bet.id);
      const mapped: RevealPick[] = picks.map((p) => ({
        user_id: p.user_id,
        display_name: p.display_name,
        avatar_id: p.avatar_id,
        label: p.selected_option === 'other'
          ? (p.other_text?.trim() ? p.other_text.trim() : 'אחר')
          : p.selected_option,
      }));
      setRevealPicks(mapped);
    } catch (err) {
      console.warn('fetchSideBetPicksForGroup failed:', err);
    } finally {
      setRevealLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const globalBets = await fetchGlobalSideBets();
        const betIds = globalBets.map(b => b.id);
        const [picks, membersRes] = await Promise.all([
          fetchGlobalSideBetPicks(betIds, user.id, groupId),
          supabase.from('group_members').select('user_id').eq('group_id', groupId),
        ]);
        setTotalMembers(membersRes.data?.length ?? 0);

        // Pre-fill other texts from existing picks
        const initialOtherTexts: Record<string, string> = {};
        globalBets.forEach(b => {
          const pick = picks[b.id];
          if (pick?.selected_option === 'other' && pick.other_text) {
            initialOtherTexts[b.id] = pick.other_text;
          }
        });

        setBets(globalBets);
        setPicksMap(picks);
        setOtherTexts(initialOtherTexts);

        // Auto-collapse rounds that have no open bets
        const openRounds = new Set(globalBets.filter(b => b.status === 'open').map(b => b.round_number));
        const allRounds = new Set(globalBets.map(b => b.round_number));
        const toCollapse = new Set<number>();
        allRounds.forEach(r => {
          if (!openRounds.has(r)) toCollapse.add(r);
        });
        setCollapsedRounds(toCollapse);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handlePick = async (bet: GlobalSideBet, option: string) => {
    if (!user || bet.status !== 'open') return;

    if (option === 'other') {
      // Just select the button; save happens on blur/Enter of text input
      setPicksMap(prev => ({
        ...prev,
        [bet.id]: {
          id: `local-${Date.now()}`,
          side_bet_id: bet.id,
          user_id: user.id,
          selected_option: 'other',
          other_text: prev[bet.id]?.other_text ?? null,
          points_earned: 0,
          created_at: new Date().toISOString(),
        },
      }));
      // Focus the text input
      setTimeout(() => otherInputRefs.current[bet.id]?.focus(), 50);
      return;
    }

    setSavingPick(bet.id);
    try {
      // Optimistic update
      setPicksMap(prev => ({
        ...prev,
        [bet.id]: {
          id: `local-${Date.now()}`,
          side_bet_id: bet.id,
          user_id: user.id,
          selected_option: option,
          other_text: null,
          points_earned: 0,
          created_at: new Date().toISOString(),
        },
      }));
      await pickGlobalSideBet(bet.id, user.id, groupId, option);
    } catch (err) {
      console.error('Failed to save pick:', err);
    } finally {
      setSavingPick(null);
    }
  };

  const handleOtherSubmit = async (bet: GlobalSideBet) => {
    if (!user || bet.status !== 'open') return;
    const text = otherTexts[bet.id]?.trim();
    if (!text) return;

    setSavingPick(bet.id);
    try {
      setPicksMap(prev => ({
        ...prev,
        [bet.id]: {
          id: `local-${Date.now()}`,
          side_bet_id: bet.id,
          user_id: user.id,
          selected_option: 'other',
          other_text: text,
          points_earned: 0,
          created_at: new Date().toISOString(),
        },
      }));
      await pickGlobalSideBet(bet.id, user.id, groupId, 'other', text);
    } catch (err) {
      console.error('Failed to save other pick:', err);
    } finally {
      setSavingPick(null);
    }
  };

  const toggleRound = (round: number) => {
    setCollapsedRounds(prev => {
      const next = new Set(prev);
      if (next.has(round)) next.delete(round);
      else next.add(round);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  // Group bets by round_number, keeping round order
  const roundNumbers = Array.from(new Set(bets.map(b => b.round_number))).sort((a, b) => a - b);
  const betsByRound: Record<number, GlobalSideBet[]> = {};
  roundNumbers.forEach(r => {
    betsByRound[r] = bets.filter(b => b.round_number === r);
  });

  const hasAnyBets = bets.length > 0;

  return (
    <div className="px-4 py-6" dir="rtl">
      {/* Explainer banner */}
      <div
        className="rounded-xl px-4 py-3 mb-6"
        style={{
          border: '1px solid rgba(196,164,103,0.25)',
          background: 'rgba(196,164,103,0.06)',
        }}
      >
        <p className="text-sm text-[#C4A467]/80">
          הימורי צד - ניחושי סטטיסטיקות שוברי שוויון שיעשו את ההבדל בדירוג
        </p>
      </div>

      {/* Empty state */}
      {!hasAnyBets && (
        <p className="text-sm text-muted-foreground text-center py-8">אין הימורי אווירה פתוחים כרגע</p>
      )}

      {/* Rounds */}
      {roundNumbers.map(round => {
        const roundBets = betsByRound[round];
        const roundLabel = ROUND_LABELS[round] ?? `סיבוב ${round}`;
        const betCount = roundBets.length;
        const isCollapsed = collapsedRounds.has(round);

        return (
          <div key={round} className="mb-8">
            {/* Round header */}
            <button
              onClick={() => toggleRound(round)}
              className="w-full flex items-center justify-between gap-2 mb-4 sticky top-0 z-10 bg-background/80 backdrop-blur-sm py-2"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#C4A467]" />
                <span className="text-sm font-medium uppercase tracking-[0.15em] text-[#C4A467]">
                  {roundLabel}
                </span>
                <span className="text-xs bg-[#C4A467]/15 text-[#C4A467] px-2 py-0.5 rounded-full">
                  {betCount}
                </span>
              </div>
              {isCollapsed ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-3 overflow-hidden"
                >
                  {roundBets.map((bet, index) => {
                    const pick = picksMap[bet.id];
                    const userSelected = pick?.selected_option;
                    const isSaving = savingPick === bet.id;
                    const isRevealable =
                      bet.status === 'locked' ||
                      bet.status === 'pending_review' ||
                      bet.status === 'resolved';

                    // Points badge text — fixed value, only awarded if guess is correct
                    const pointsBadge = bet.points_other > 0
                      ? `${bet.points_hit}/${bet.points_other} נק׳ בניחוש נכון`
                      : `${bet.points_hit} נק׳ בניחוש נכון`;

                    const handleCardClick = () => {
                      if (isRevealable) handleRevealBet(bet);
                    };

                    return (
                      <motion.div
                        key={bet.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        {...(isRevealable && {
                          role: 'button',
                          tabIndex: 0,
                          onClick: handleCardClick,
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCardClick();
                            }
                          },
                        })}
                        className={[
                          'relative bg-card rounded-2xl overflow-hidden',
                          bet.status === 'resolved' ? 'opacity-[0.85]' : '',
                          isRevealable
                            ? 'cursor-pointer px-4 pt-12 pb-4 border border-violet-400/40 shadow-[0_0_14px_rgba(167,139,250,0.15),inset_0_0_18px_rgba(167,139,250,0.08)] transition-colors'
                            : 'p-4',
                        ].join(' ')}
                        style={isRevealable ? undefined : { border: '1px solid rgba(196,164,103,0.25)' }}
                      >
                        {isRevealable && <RevealHighlight variant="full" />}
                        {/* Title row + status badge */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <p className="text-sm font-medium text-right flex-1">{bet.title_he}</p>
                          </div>
                          <div className="shrink-0">
                            {bet.status === 'open' && !userSelected && (
                              <span className="border border-accent text-accent text-[10px] px-2 py-0.5 rounded-full">
                                ממתין להימור
                              </span>
                            )}
                            {bet.status === 'locked' && (
                              <span className="flex items-center gap-1 bg-muted/30 text-muted-foreground text-[10px] px-2 py-0.5 rounded-full">
                                <Lock className="w-2.5 h-2.5" />
                                נעול
                              </span>
                            )}
                            {bet.status === 'pending_review' && (
                              <span className="flex items-center gap-1 text-yellow-400/90 bg-yellow-400/10 text-[10px] px-2 py-0.5 rounded-full">
                                <Lock className="w-2.5 h-2.5" />
                                ממתין לאישור
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Options */}
                        <div className="mt-3 space-y-2">
                          {/* Open state: interactive buttons */}
                          {bet.status === 'open' && (
                            <>
                              {bet.options.map(option => {
                                const isSelected = userSelected === option;
                                return (
                                  <button
                                    key={option}
                                    onClick={() => handlePick(bet, option)}
                                    disabled={isSaving}
                                    className={[
                                      'cursor-pointer rounded-lg px-3 py-2 text-sm w-full transition-all duration-150',
                                      isSelected
                                        ? 'border border-accent bg-accent/10 text-accent'
                                        : 'bg-secondary/50 text-foreground hover:bg-secondary/80',
                                    ].join(' ')}
                                  >
                                    <span className="flex items-center justify-between gap-2">
                                      <span className="text-right flex-1">
                                        {isSaving && isSelected ? (
                                          <span className="flex items-center justify-end gap-1.5">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            {option}
                                          </span>
                                        ) : (
                                          option
                                        )}
                                      </span>
                                      <AnimatePresence>
                                        {isSelected && !isSaving && (
                                          <motion.span
                                            key="pts"
                                            initial={{ opacity: 0, scale: 0.6 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.6 }}
                                            transition={{ duration: 0.15, type: 'spring', stiffness: 400, damping: 25 }}
                                            className="shrink-0 text-[10px] font-bold text-[#C4A467] bg-[#C4A467]/15 rounded-full px-2 py-0.5 leading-none whitespace-nowrap"
                                          >
                                            +{bet.points_hit} נק׳
                                          </motion.span>
                                        )}
                                      </AnimatePresence>
                                    </span>
                                  </button>
                                );
                              })}
                              {/* "Other" button */}
                              {bet.has_other && (
                                <>
                                  <button
                                    onClick={() => handlePick(bet, 'other')}
                                    disabled={isSaving}
                                    className={[
                                      'cursor-pointer rounded-lg px-3 py-2 text-sm w-full transition-all duration-150',
                                      userSelected === 'other'
                                        ? 'border border-accent bg-accent/10 text-accent'
                                        : 'bg-secondary/50 text-foreground hover:bg-secondary/80',
                                    ].join(' ')}
                                  >
                                    <span className="flex items-center justify-between gap-2">
                                      <span className="text-right flex-1">אחר</span>
                                      <AnimatePresence>
                                        {userSelected === 'other' && !isSaving && (
                                          <motion.span
                                            key="pts-other"
                                            initial={{ opacity: 0, scale: 0.6 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.6 }}
                                            transition={{ duration: 0.15, type: 'spring', stiffness: 400, damping: 25 }}
                                            className="shrink-0 text-[10px] font-bold text-[#C4A467] bg-[#C4A467]/15 rounded-full px-2 py-0.5 leading-none whitespace-nowrap"
                                          >
                                            +{bet.points_other > 0 ? bet.points_other : bet.points_hit} נק׳
                                          </motion.span>
                                        )}
                                      </AnimatePresence>
                                    </span>
                                  </button>
                                  {userSelected === 'other' && (
                                    <input
                                      ref={el => { otherInputRefs.current[bet.id] = el; }}
                                      type="text"
                                      dir="rtl"
                                      value={otherTexts[bet.id] ?? ''}
                                      onChange={e => setOtherTexts(prev => ({ ...prev, [bet.id]: e.target.value }))}
                                      onBlur={() => handleOtherSubmit(bet)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        }
                                      }}
                                      placeholder="הקלד שם שחקן..."
                                      className="w-full rounded-lg px-3 py-2 text-sm bg-secondary/50 border border-accent/40 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
                                    />
                                  )}
                                </>
                              )}
                            </>
                          )}

                          {/* Locked / pending_review state: non-interactive, user pick highlighted */}
                          {(bet.status === 'locked' || bet.status === 'pending_review') && (
                            <>
                              {bet.options.map(option => {
                                const isUserPick = userSelected === option;
                                return (
                                  <div
                                    key={option}
                                    className={[
                                      'rounded-lg px-3 py-2 text-sm text-right w-full',
                                      isUserPick
                                        ? 'border border-accent bg-accent/10 text-accent'
                                        : 'bg-secondary/50 text-foreground opacity-50',
                                    ].join(' ')}
                                  >
                                    {option}
                                  </div>
                                );
                              })}
                              {bet.has_other && (
                                <div
                                  className={[
                                    'rounded-lg px-3 py-2 text-sm text-right w-full',
                                    userSelected === 'other'
                                      ? 'border border-accent bg-accent/10 text-accent'
                                      : 'bg-secondary/50 text-foreground opacity-50',
                                  ].join(' ')}
                                >
                                  {userSelected === 'other' && pick?.other_text
                                    ? `אחר: ${pick.other_text}`
                                    : 'אחר'}
                                </div>
                              )}
                            </>
                          )}

                          {/* Resolved state: correct answer green, user result icon */}
                          {bet.status === 'resolved' && (
                            <>
                              {bet.options.map(option => {
                                const isCorrect = option === bet.correct_answer;
                                const isUserPick = userSelected === option;
                                return (
                                  <div
                                    key={option}
                                    className={[
                                      'rounded-lg px-3 py-2 text-sm text-right w-full flex items-center justify-between',
                                      isCorrect
                                        ? 'bg-green-500/10 border border-green-500/40 text-green-400'
                                        : 'bg-secondary/50 text-foreground opacity-50',
                                    ].join(' ')}
                                  >
                                    <span>{option}</span>
                                    {isUserPick && isCorrect && <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />}
                                    {isUserPick && !isCorrect && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                                  </div>
                                );
                              })}
                              {/* Other option in resolved state */}
                              {bet.has_other && (
                                <div
                                  className={[
                                    'rounded-lg px-3 py-2 text-sm text-right w-full flex items-center justify-between',
                                    bet.correct_answer === 'other' || (userSelected === 'other' && bet.correct_answer === pick?.other_text)
                                      ? 'bg-green-500/10 border border-green-500/40 text-green-400'
                                      : 'bg-secondary/50 text-foreground opacity-50',
                                  ].join(' ')}
                                >
                                  <span>
                                    {userSelected === 'other' && pick?.other_text
                                      ? `אחר: ${pick.other_text}`
                                      : 'אחר'}
                                  </span>
                                  {userSelected === 'other' && pick && (() => {
                                    const userOther = pick.other_text;
                                    const isOtherCorrect = bet.correct_answer === 'other' || bet.correct_answer === userOther;
                                    return isOtherCorrect
                                      ? <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                                      : <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
                                  })()}
                                </div>
                              )}
                              {/* If correct_answer is a custom text (not one of the options), display it */}
                              {bet.correct_answer && bet.correct_answer !== 'other' &&
                                !bet.options.includes(bet.correct_answer) && (
                                  <p className="text-xs text-green-400/80 text-right mt-1">
                                    תשובה נכונה: {bet.correct_answer}
                                  </p>
                                )}
                            </>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/40">
                          <div className="flex items-center gap-3">
                            {/* Points earned badge for resolved */}
                            {bet.status === 'resolved' && pick && pick.points_earned > 0 && (
                              <span className="text-xs text-green-400 font-medium">
                                +{pick.points_earned} נק׳
                              </span>
                            )}
                            {/* Deadline for open */}
                            {bet.status === 'open' && (
                              <span className="text-xs text-muted-foreground">{formatDeadline(bet.deadline)}</span>
                            )}
                          </div>
                          <span className="text-xs text-accent font-medium flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {pointsBadge}
                          </span>
                        </div>

                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <PicksRevealSheet
        open={revealBet !== null}
        onClose={() => setRevealBet(null)}
        title={revealBet?.title_he ?? ''}
        subtitle={revealLoading ? 'טוען...' : 'בחירות חברי הקבוצה'}
        picks={revealPicks}
        currentUserId={user?.id}
        totalMembers={totalMembers}
      />
    </div>
  );
};

export default SideBetsFull;
